import React, { useState, useRef, useEffect } from 'react';

// 视频列表
const videoList = [
  { id: 1, name: '(云E投屏)投屏电视演示视频', path: '/assets/mp4/c4120ae47a8f5a54dd6f4003d1f0f303.mp4' },
  { id: 2, name: '(云E投屏)账号登录演示视频', path: '/assets/mp4/ye_login_password.mp4' },
  { id: 3, name: '(云E投屏)账号注册演示视频', path: '/assets/mp4/ye_register.mp4' },
  // { id: 2, name: '1_U3Let\'sChant.mp4', path: '/assets/mp4/1_U3Let\'sChant.mp4' },
  // { id: 3, name: '2_U3Let\'sChant.mp4', path: '/assets/mp4/2_U3Let\'sChant.mp4' },
];

const VideoPlayer: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentVideo, setCurrentVideo] = useState(videoList[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0); // 默认音量为0
  const [isMuted, setIsMuted] = useState(true); // 默认静音
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 禁止页面缩放
  useEffect(() => {
    // 禁止键盘缩放 (Ctrl + +/-)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && (e.key === '+' || e.key === '-' || e.key === '0')) {
        e.preventDefault();
        return;
      }
      // 禁止 Ctrl + 滚轮
      if (e.ctrlKey && (e.key === '=' || e.key === '_')) {
        e.preventDefault();
        return;
      }
    };

    // 禁止鼠标滚轮缩放 (Ctrl + 滚轮)
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        return false;
      }
    };

    // 禁止双指缩放 (触摸设备)
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
        return false;
      }
    };

    // 禁止手势缩放 (Safari)
    const handleGestureStart = (e: Event) => {
      e.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('gesturestart', handleGestureStart);
    document.addEventListener('gesturechange', handleGestureStart);
    document.addEventListener('gestureend', handleGestureStart);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('gesturestart', handleGestureStart);
      document.removeEventListener('gesturechange', handleGestureStart);
      document.removeEventListener('gestureend', handleGestureStart);
    };
  }, []);

  // 切换视频
  const switchVideo = (video: typeof videoList[0]) => {
    if (currentVideo.id === video.id) {
      togglePlay();
      return;
    }
    
    setCurrentVideo(video);
    setCurrentTime(0);
    setProgress(0);
    setIsPlaying(false);
    
    setTimeout(() => {
      const videoElement = videoRef.current;
      if (videoElement) {
        videoElement.muted = true; // 确保新视频也是静音
        videoElement.volume = 0;
        videoElement.play().catch(err => console.log('自动播放失败:', err));
      }
    }, 100);
  };

  // 格式化时间
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 播放/暂停
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(err => console.log('播放失败:', err));
    } else {
      video.pause();
    }
  };

  // 快进/快退
  const seek = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.currentTime + seconds, video.duration || 0));
  };

  // 进度条拖动
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const seekTime = (parseFloat(e.target.value) / 100) * video.duration;
    video.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  // 音量调节
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const vol = parseFloat(e.target.value);
    video.volume = vol;
    setVolume(vol);
    setIsMuted(vol === 0);
    if (vol > 0) {
      video.muted = false;
    }
  };

  // 静音切换
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isMuted) {
      // 取消静音 - 恢复到之前的音量或默认音量
      const newVol = volume === 0 ? 0.8 : volume;
      video.volume = newVol;
      video.muted = false;
      setVolume(newVol);
      setIsMuted(false);
    } else {
      // 静音
      video.muted = true;
      setVolume(0);
      setIsMuted(true);
    }
  };

  // 全屏切换
  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (!document.fullscreenElement) {
      video.requestFullscreen?.().catch(err => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(err => {});
      setIsFullscreen(false);
    }
  };

  // 防止右键下载
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // 防止拖拽下载
  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      
      switch(e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowRight':
          e.preventDefault();
          seek(5);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seek(-5);
          break;
        case 'KeyF':
          toggleFullscreen();
          break;
        case 'KeyM':
          toggleMute();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMuted, volume]);

  // 视频事件监听
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // 默认静音
    video.muted = true;
    video.volume = 0;

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [currentVideo]);

  return (
    <div className="player-wrapper">
      <div className="player-container">
        <div className="player-main">
          {/* 视频播放区域 */}
          <div className="video-container">
            <video
              ref={videoRef}
              className="video-element"
              src={currentVideo.path}
              preload="metadata"
              playsInline
              muted // 添加 muted 属性强制静音
              onContextMenu={handleContextMenu}
              onDragStart={handleDragStart}
              controlsList="nodownload noremoteplayback"
              disablePictureInPicture
            />
            
            {!isPlaying && (
              <button 
                className="center-play-btn"
                onClick={togglePlay}
                aria-label="播放"
              >
                ▶
              </button>
            )}
          </div>

          {/* 控制栏 */}
          <div className="controls">
            <div className="progress-section">
              <span className="time-display">{formatTime(currentTime)}</span>
              <input
                type="range"
                className="progress-bar"
                value={progress}
                min="0"
                max="100"
                step="0.1"
                onChange={handleProgressChange}
                aria-label="播放进度"
              />
              <span className="time-display">{formatTime(duration)}</span>
            </div>

            <div className="controls-section">
              <div className="controls-left">
                <button className="ctrl-btn" onClick={togglePlay} aria-label="播放/暂停">
                  {isPlaying ? '⏸' : '▶'}
                </button>
                <button className="ctrl-btn" onClick={() => seek(-10)} aria-label="快退10秒">
                  ⏪
                </button>
                <button className="ctrl-btn" onClick={() => seek(10)} aria-label="快进10秒">
                  ⏩
                </button>
                <button className="ctrl-btn" onClick={toggleMute} aria-label="静音">
                  {isMuted ? '🔇' : '🔊'}
                </button>
                <div className="volume-control">
                  <input
                    type="range"
                    className="volume-slider"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={handleVolumeChange}
                    aria-label="音量"
                  />
                </div>
              </div>

              <div className="controls-right">
                <button className="ctrl-btn" onClick={toggleFullscreen} aria-label="全屏">
                  {isFullscreen ? '⛶' : '⛶'}
                </button>
              </div>
            </div>
          </div>

          {/* 键盘快捷键提示 */}
          <div className="shortcuts-hint">
            <span>空格: 播放/暂停</span>
            <span>← →: 快退/快进 5秒</span>
            <span>F: 全屏</span>
            <span>M: 静音</span>
          </div>
        </div>

        {/* 播放列表 */}
        <div className="playlist">
          <h3 className="playlist-title">📋 播放列表</h3>
          <div className="playlist-items">
            {videoList.map((video) => (
              <div
                key={video.id}
                className={`playlist-item ${currentVideo.id === video.id ? 'active' : ''}`}
                onClick={() => switchVideo(video)}
              >
                <span className="playlist-item-name">{video.name}</span>
                <span className="playlist-item-status">
                  {currentVideo.id === video.id ? (isPlaying ? '▶ 播放中' : '⏸ 已暂停') : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="playlist-footer">
            <span>共 {videoList.length} 个视频</span>
          </div>
        </div>
      </div>

      <style>{`
        /* 禁止缩放 */
        html, body {
          touch-action: manipulation;
          -webkit-text-size-adjust: none;
          -moz-text-size-adjust: none;
          -ms-text-size-adjust: none;
          text-size-adjust: none;
        }

        .player-wrapper {
          padding: 40px 20px;
          background: #0a0d12;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          touch-action: pan-y;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }

        .player-container {
          max-width: 1200px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 24px;
          background: #141a24;
          border-radius: 32px;
          padding: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
          touch-action: manipulation;
        }

        .player-main {
          min-width: 0;
        }

        .video-container {
          position: relative;
          width: 100%;
          background: #000;
          border-radius: 16px;
          overflow: hidden;
          aspect-ratio: 16 / 9;
          touch-action: none;
        }

        .video-element {
          width: 100%;
          height: 100%;
          display: block;
          background: #000;
          cursor: pointer;
          object-fit: contain;
          touch-action: none;
        }

        /* 禁止选择文本 */
        .player-wrapper * {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }

        /* 隐藏原生控件 */
        .video-element::-webkit-media-controls {
          display: none !important;
        }
        .video-element::-webkit-media-controls-enclosure {
          display: none !important;
        }

        /* 中央播放按钮 */
        .center-play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 40px;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          padding-left: 8px;
          z-index: 10;
          touch-action: manipulation;
        }

        .center-play-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%) scale(1.1);
        }

        /* 控制栏 */
        .controls {
          margin-top: 16px;
          padding: 16px 20px;
          background: #1e2632;
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .progress-section {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .progress-bar {
          flex: 1;
          height: 4px;
          -webkit-appearance: none;
          appearance: none;
          background: #2f3a4a;
          border-radius: 10px;
          outline: none;
          cursor: pointer;
        }

        .progress-bar::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          background: #3b82f6;
          border-radius: 50%;
          cursor: pointer;
          transition: 0.1s;
        }

        .progress-bar::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }

        .progress-bar::-moz-range-thumb {
          width: 14px;
          height: 14px;
          background: #3b82f6;
          border-radius: 50%;
          border: none;
          cursor: pointer;
        }

        .time-display {
          font-size: 14px;
          color: #9aa9bb;
          min-width: 50px;
          font-variant-numeric: tabular-nums;
        }

        .controls-section {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .controls-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .controls-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ctrl-btn {
          background: transparent;
          border: none;
          color: #c8d0dc;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 18px;
          padding: 0;
          touch-action: manipulation;
        }

        .ctrl-btn:hover {
          background: rgba(255, 255, 255, 0.06);
          color: #ffffff;
        }

        .ctrl-btn:active {
          transform: scale(0.9);
        }

        .volume-control {
          width: 70px;
        }

        .volume-slider {
          width: 100%;
          height: 3px;
          -webkit-appearance: none;
          appearance: none;
          background: #2f3a4a;
          border-radius: 10px;
          outline: none;
          cursor: pointer;
        }

        .volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 12px;
          height: 12px;
          background: #c8d0dc;
          border-radius: 50%;
          cursor: pointer;
        }

        .volume-slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          background: #c8d0dc;
          border-radius: 50%;
          border: none;
          cursor: pointer;
        }

        .shortcuts-hint {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-top: 12px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 8px;
          font-size: 12px;
          color: #6a7b90;
        }

        .shortcuts-hint span {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .shortcuts-hint span::before {
          content: '⌨';
          opacity: 0.5;
          margin-right: 4px;
        }

        /* 播放列表样式 */
        .playlist {
          background: #1a232f;
          border-radius: 16px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          max-height: 600px;
          display: flex;
          flex-direction: column;
        }

        .playlist-title {
          color: #b6c3d4;
          font-size: 16px;
          font-weight: 500;
          margin: 0 0 16px 0;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .playlist-items {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .playlist-items::-webkit-scrollbar {
          width: 4px;
        }

        .playlist-items::-webkit-scrollbar-track {
          background: transparent;
        }

        .playlist-items::-webkit-scrollbar-thumb {
          background: #2f3a4a;
          border-radius: 2px;
        }

        .playlist-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          color: #8a9bb0;
          font-size: 14px;
          touch-action: manipulation;
        }

        .playlist-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #b6c3d4;
        }

        .playlist-item.active {
          background: rgba(59, 130, 246, 0.15);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .playlist-item-name {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .playlist-item-status {
          font-size: 12px;
          color: #60a5fa;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .playlist-footer {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 12px;
          color: #6a7b90;
          text-align: center;
        }

        @media (max-width: 900px) {
          .player-container {
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px;
          }
          
          .playlist {
            max-height: 200px;
          }
        }

        @media (max-width: 700px) {
          .player-wrapper {
            padding: 12px;
          }
          .player-container {
            padding: 12px;
            border-radius: 20px;
          }
          .controls {
            padding: 12px;
          }
          .controls-left {
            gap: 4px;
          }
          .ctrl-btn {
            width: 32px;
            height: 32px;
            font-size: 16px;
          }
          .volume-control {
            width: 50px;
          }
          .time-display {
            font-size: 12px;
            min-width: 40px;
          }
          .shortcuts-hint {
            font-size: 10px;
            gap: 8px;
          }
          .center-play-btn {
            width: 60px;
            height: 60px;
            font-size: 28px;
          }
          .playlist-title {
            font-size: 14px;
          }
          .playlist-item {
            font-size: 13px;
            padding: 8px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoPlayer;